import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { useDataProvider, useNotify } from "react-admin";
import moment from "moment";
import "moment/locale/es";

const paymentMethods = [
  { id: "Efectivo", name: "Efectivo" },
  { id: "Tarjeta", name: "Tarjeta" },
  { id: "QR", name: "QR" },
];

const MultiReservasModal = ({ open, onClose, initialDate, branch, serviceMethod: _serviceMethod }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Estados principales
  const [selectedBranch, setSelectedBranch] = useState(branch || null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(initialDate ? moment(initialDate).format("YYYY-MM-DD") : "");
  const [serviceBookings, setServiceBookings] = useState([]);

  // Estados de datos
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Estados de carga
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Estados de búsqueda
  const [customerSearchText, setCustomerSearchText] = useState("");

  // Estados de WhatsApp
  const [whatsappSettings, setWhatsappSettings] = useState({
    sendOnCreate: true,
    sendOnFinish: true,
    sendQRCode: true,
    sendOnCancel: true,
  });

  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;
  const isAdminSucursal = profile?.role === "admin-sucursal";
  const branchId = profile?.branch?._id; // Extraer solo el ID para evitar loop

  // Cargar sucursales
  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        // Si es admin-sucursal, solo cargar su sucursal
        if (isAdminSucursal && branchId) {
          const branchData = await dataProvider.getOne("enterprise/branches", {
            id: branchId,
          });
          setBranches([branchData.data]);
          setSelectedBranch(branchData.data);
        } else {
          // Si es proveedor/admin, cargar todas las sucursales
          const { data } = await dataProvider.getList("enterprise/branches", {
            filter: { status: true },
            pagination: { page: 1, perPage: 100 },
            sort: { field: "name", order: "ASC" },
          });
          setBranches(data);

          // Si branch viene como prop, intentar encontrarlo
          if (branch) {
            const branchIdProp = typeof branch === "object" ? branch._id || branch.id : branch;
            const foundBranch = data.find((b) => b._id === branchIdProp || b.id === branchIdProp);
            if (foundBranch) {
              setSelectedBranch(foundBranch);
            }
          }
        }
      } catch (error) {
        notify("Error al cargar sucursales", { type: "error" });
      } finally {
        setLoadingBranches(false);
      }
    };

    if (open) {
      fetchBranches();
    }
  }, [open, branch, dataProvider, notify, isAdminSucursal, branchId]);

  // Cargar clientes
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const filter = {
          $or: [
            { fullName: { $regex: customerSearchText, $options: "i" } },
            { phone: { $regex: customerSearchText, $options: "i" } },
          ],
        };

        if (providerId) {
          filter.providerId = providerId;
        }

        const { data } = await dataProvider.getList("enterprise/customers", {
          filter,
          pagination: { page: 1, perPage: 25 },
          sort: { field: "fullName", order: "ASC" },
        });
        setCustomers(data);
      } catch (error) {
        notify("Error al cargar clientes", { type: "error" });
      } finally {
        setLoadingCustomers(false);
      }
    };

    if (open && customerSearchText.length >= 0) {
      const debounce = setTimeout(() => {
        fetchCustomers();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [open, customerSearchText, providerId, dataProvider, notify]);

  // Cargar servicios cuando se selecciona una sucursal
  useEffect(() => {
    const fetchServices = async () => {
      if (!selectedBranch) {
        setServices([]);
        return;
      }

      setLoadingServices(true);
      try {
        const { data: allServices } = await dataProvider.getList("service", {
          filter: { enabled: true },
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "name", order: "ASC" },
        });

        // Filtrar servicios por sucursal
        const branchServices = allServices.filter((service) =>
          selectedBranch.services?.includes(service._id || service.id)
        );

        setServices(branchServices);
      } catch (error) {
        notify("Error al cargar servicios", { type: "error" });
      } finally {
        setLoadingServices(false);
      }
    };

    if (open && selectedBranch) {
      fetchServices();
    }
  }, [open, selectedBranch, dataProvider, notify]);

  // Cargar empleados cuando se selecciona una sucursal
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!selectedBranch) {
        setEmployees([]);
        return;
      }

      setLoadingEmployees(true);
      try {
        const { data } = await dataProvider.getList("employeesAll", {
          filter: {
            providerId: providerId,
            branchUrl: selectedBranch._id || selectedBranch.id,
            state: true,
          },
          pagination: { page: 1, perPage: 100 },
          sort: { field: "first_name", order: "ASC" },
        });
        setEmployees(data);
      } catch (error) {
        notify("Error al cargar empleados", { type: "error" });
      } finally {
        setLoadingEmployees(false);
      }
    };

    if (open && selectedBranch) {
      fetchEmployees();
    }
  }, [open, selectedBranch, providerId, dataProvider, notify]);

  // Agregar nuevo servicio a la lista
  const handleAddService = () => {
    setServiceBookings([
      ...serviceBookings,
      {
        id: Date.now(),
        service: null,
        employee: null,
        date: selectedDate,
        hour: "",
        availableHours: [],
        paymentMethod: "Efectivo",
      },
    ]);
  };

  // Eliminar servicio de la lista
  const handleRemoveService = (id) => {
    setServiceBookings(serviceBookings.filter((sb) => sb.id !== id));
  };

  // Actualizar servicio seleccionado
  const handleServiceChange = (id, service) => {
    setServiceBookings(
      serviceBookings.map((sb) => {
        if (sb.id === id) {
          return { ...sb, service, employee: null, hour: "", availableHours: [] };
        }
        return sb;
      })
    );
  };

  // Actualizar empleado seleccionado y cargar horarios disponibles
  const handleEmployeeChange = async (id, employee) => {
    const booking = serviceBookings.find((sb) => sb.id === id);
    if (!booking || !booking.service || !selectedDate) return;

    setServiceBookings(
      serviceBookings.map((sb) => {
        if (sb.id === id) {
          return { ...sb, employee, hour: "", availableHours: [] };
        }
        return sb;
      })
    );

    // Cargar horarios disponibles
    try {
      const response = await dataProvider.create(`dateAvailability/${providerId}`, {
        data: {
          date: moment(selectedDate).toDate(),
          estimatedTime: booking.service.unitEstimatedWorkMinutes || 30,
          isInBranch: true,
          branch: selectedBranch._id || selectedBranch.id,
          employee: employee._id || employee.id,
          serviceCart: [booking.service._id || booking.service.id],
        },
      });

      const availability = response.data.data?.availability || response.data.availability || [];

      // Filtrar horarios pasados si es el día actual
      const today = moment();
      const selectedDateMoment = moment(selectedDate);
      const isSameDay = today.isSame(selectedDateMoment, "day");

      const availableHours = availability
        .map((dateTime) => {
          const m = moment(dateTime);

          // Si es el día actual, filtrar horas que ya pasaron
          if (isSameDay && m.hours() <= today.hours()) {
            return null; // Filtrar esta hora
          }

          return {
            id: m.hours() + m.minutes() / 60,
            name: m.format("HH:mm"),
            value: dateTime,
          };
        })
        .filter(Boolean); // Eliminar valores null

      setServiceBookings(
        serviceBookings.map((sb) => {
          if (sb.id === id) {
            return { ...sb, employee, availableHours };
          }
          return sb;
        })
      );
    } catch (error) {
      notify("Error al cargar horarios disponibles", { type: "error" });
    }
  };

  // Actualizar hora seleccionada
  const handleHourChange = (id, hour) => {
    setServiceBookings(
      serviceBookings.map((sb) => {
        if (sb.id === id) {
          return { ...sb, hour };
        }
        return sb;
      })
    );
  };

  // Actualizar método de pago
  const handlePaymentMethodChange = (id, paymentMethod) => {
    setServiceBookings(
      serviceBookings.map((sb) => {
        if (sb.id === id) {
          return { ...sb, paymentMethod };
        }
        return sb;
      })
    );
  };

  // Recalcular horarios cuando cambia la fecha global
  useEffect(() => {
    const recalculateHours = async () => {
      // Recalcular horarios para cada servicio que ya tiene empleado seleccionado
      for (const booking of serviceBookings) {
        if (booking.employee && booking.service && selectedDate) {
          try {
            const response = await dataProvider.create(`dateAvailability/${providerId}`, {
              data: {
                date: moment(selectedDate).toDate(),
                estimatedTime: booking.service.unitEstimatedWorkMinutes || 30,
                isInBranch: true,
                branch: selectedBranch._id || selectedBranch.id,
                employee: booking.employee._id || booking.employee.id,
                serviceCart: [booking.service._id || booking.service.id],
              },
            });

            const availability = response.data.data?.availability || response.data.availability || [];

            // Filtrar horarios pasados si es el día actual
            const today = moment();
            const selectedDateMoment = moment(selectedDate);
            const isSameDay = today.isSame(selectedDateMoment, "day");

            const availableHours = availability
              .map((dateTime) => {
                const m = moment(dateTime);

                // Si es el día actual, filtrar horas que ya pasaron
                if (isSameDay && m.hours() <= today.hours()) {
                  return null;
                }

                return {
                  id: m.hours() + m.minutes() / 60,
                  name: m.format("HH:mm"),
                  value: dateTime,
                };
              })
              .filter(Boolean);

            // Actualizar solo este servicio
            setServiceBookings((prev) =>
              prev.map((sb) => {
                if (sb.id === booking.id) {
                  return { ...sb, hour: "", availableHours };
                }
                return sb;
              })
            );
          } catch (error) {
            // Error silencioso al recalcular horarios
            notify("Error al recalcular horarios", { type: "warning" });
          }
        }
      }
    };

    if (selectedDate && serviceBookings.length > 0) {
      recalculateHours();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]); // Solo cuando cambia la fecha, evitar loop infinito

  // Validar formulario
  const isFormValid = () => {
    if (!selectedCustomer || !selectedBranch || serviceBookings.length === 0) {
      return false;
    }

    return serviceBookings.every((sb) => sb.service && sb.employee && sb.date && sb.hour);
  };

  // Calcular total
  const calculateTotal = () => {
    return serviceBookings.reduce((total, sb) => {
      return total + (sb.service?.unitPrice || 0);
    }, 0);
  };

  // Enviar formulario
  const handleSubmit = async () => {
    if (!isFormValid()) {
      notify("Por favor complete todos los campos requeridos", { type: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const createdBookings = [];

      // Crear cada reserva individualmente usando el mismo formato que el formulario de crear reserva
      for (let i = 0; i < serviceBookings.length; i++) {
        const sb = serviceBookings[i];
        const hourMoment = moment(sb.hour.value);

        // Formato exacto que usa el formulario de crear reserva individual
        const profile = JSON.parse(localStorage.getItem("profile"));
        const isAdminSucursal = profile?.role === "admin-sucursal";

        // Para admin-sucursal, obtener datos del proveedor desde profile.employer
        // Para proveedor, usar profile directamente
        let providerData = profile;
        if (isAdminSucursal) {
          // Intentar obtener datos completos del proveedor
          try {
            const providerResponse = await dataProvider.getOne("providers", {
              id: profile.employer._id,
            });
            providerData = providerResponse.data;
          } catch (error) {
            // Si falla, usar profile.employer como fallback
            providerData = profile.employer;
          }
        }

        const bookingData = {
          customer: {
            _id: selectedCustomer._id || selectedCustomer.id,
            fullName: selectedCustomer.fullName,
            phone: selectedCustomer.phone,
            codePhone: selectedCustomer.codePhone || selectedCustomer.code_phone || "591",
            email: selectedCustomer.email,
          },
          branch: {
            _id: selectedBranch._id || selectedBranch.id,
            name: selectedBranch.name,
          },
          employee: {
            _id: sb.employee._id || sb.employee.id,
            fullName: sb.employee.fullName || `${sb.employee.first_name} ${sb.employee.last_name}`,
            phone: sb.employee.phone,
            photoURL: sb.employee.photoURL || sb.employee.picture,
          },
          provider: {
            ...providerData,
            name: providerData?.first_name || providerData?.name,
            logoURL: providerData?.picture,
          },
          bookingDate: hourMoment.toISOString(),
          totalEstimatedWorkMinutes: sb.service.unitEstimatedWorkMinutes,
          editedEstimatedWorkMinutes: sb.service.unitEstimatedWorkMinutes,
          createdFrom: "Manual",
          status: "Confirmado",
          isInBranch: true,
          serviceCart: [
            {
              service: {
                _id: sb.service._id || sb.service.id,
                name: sb.service.name,
                unitPrice: sb.service.unitPrice,
                unitEstimatedWorkMinutes: sb.service.unitEstimatedWorkMinutes,
              },
              quantity: 1,
              price: sb.service.unitPrice,
              estimatedWorkMinutes: sb.service.unitEstimatedWorkMinutes,
            },
          ],
          paymentInfo: {
            totalPrice: sb.service.unitPrice,
            paymentMethod: sb.paymentMethod || "Efectivo",
            couponCode: null,
          },
          whatsappSettings: {
            sendOnCreate: whatsappSettings.sendOnCreate,
            sendOnFinish: whatsappSettings.sendOnFinish,
            sendQRCode: whatsappSettings.sendQRCode,
            sendOnCancel: whatsappSettings.sendOnCancel,
          },
        };

        const response = await dataProvider.create("booking", {
          data: bookingData,
        });
        createdBookings.push(response.data);
      }

      notify(`${createdBookings.length} reserva(s) creada(s) con éxito`, { type: "success" });
      handleClose();

      // Recargar el calendario manteniendo los filtros en la URL
      setTimeout(() => {
        const branchId = selectedBranch._id || selectedBranch.id;
        // Crear un objeto Date con la fecha local (sin conversión de zona horaria)
        // selectedDate está en formato "YYYY-MM-DD"
        const [year, month, day] = selectedDate.split("-");
        const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
        const formattedDate = dateObj.toISOString();
        const currentUrl = new URL(window.location.href);
        const serviceMethod = currentUrl.searchParams.get("serviceMethod") || "Ambos";

        // Construir URL con filtros (igual que en Create.js)
        const newUrl = `${window.location.origin}${window.location.pathname}#/schedule?date=${formattedDate}&branch=${branchId}&serviceMethod=${serviceMethod}`;
        window.location.href = newUrl;
      }, 500);
    } catch (error) {
      notify(`Error al crear reservas: ${error.message || "Error desconocido"}`, { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // Cerrar modal y resetear estado
  const handleClose = () => {
    setSelectedBranch(branch || null);
    setSelectedCustomer(null);
    setSelectedDate(initialDate ? moment(initialDate).format("YYYY-MM-DD") : "");
    setServiceBookings([]);
    setCustomerSearchText("");
    onClose();
  };

  // Filtrar empleados por servicio
  const getAvailableEmployees = (service) => {
    if (!service) return employees;

    return employees.filter((emp) => {
      // Si el empleado tiene servicios disponibles definidos, verificar que incluya este servicio
      if (emp.availableServices && emp.availableServices.length > 0) {
        return emp.availableServices.includes(service._id || service.id);
      }
      // Si no tiene servicios definidos, puede atender todos
      return true;
    });
  };

  if (!open) {
    return null;
  }
  return (
    <Dialog
      open={true}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        },
      }}
      sx={{
        zIndex: 1300,
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            Multi Reservas
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loadingBranches || loadingCustomers ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Cargando datos...</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Selección de Sucursal */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Sucursal *
              </Typography>
              <Autocomplete
                key={`branches-${branches.length}`}
                options={branches}
                value={selectedBranch}
                onChange={(e, newValue) => {
                  if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  setSelectedBranch(newValue);
                  setServiceBookings([]);
                }}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option._id === value._id || option.id === value.id}
                loading={loadingBranches}
                disabled={loadingBranches || isAdminSucursal}
                componentsProps={{
                  popper: {
                    sx: { zIndex: 1400 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Seleccione una sucursal"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* Buscar o agregar cliente */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Buscar o agregar cliente *
              </Typography>
              <Autocomplete
                key={`customers-${customers.length}`}
                options={customers}
                value={selectedCustomer}
                onChange={(e, newValue) => {
                  if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  setSelectedCustomer(newValue);
                }}
                getOptionLabel={(option) => `${option.fullName}${option.phone ? ` - ${option.phone}` : ""}`}
                isOptionEqualToValue={(option, value) => option._id === value._id || option.id === value.id}
                loading={loadingCustomers}
                componentsProps={{
                  popper: {
                    sx: { zIndex: 1400 },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar cliente por nombre o teléfono"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <PersonIcon sx={{ mr: 1, color: "text.secondary" }} />,
                      endAdornment: (
                        <>
                          {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {option.fullName}
                        </Typography>
                        {option.phone && (
                          <Typography variant="caption" color="text.secondary">
                            +{option.countryCode || "591"} {option.phone}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
              />
            </Box>

            <Divider />

            {/* Fecha global */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Fecha de reserva *
              </Typography>
              <TextField
                type="date"
                fullWidth
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  // Actualizar fecha en todos los servicios
                  setServiceBookings(
                    serviceBookings.map((sb) => ({
                      ...sb,
                      date: e.target.value,
                      hour: "",
                      availableHours: [],
                    }))
                  );
                }}
                InputProps={{
                  startAdornment: <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />,
                }}
                inputProps={{
                  min: moment().format("YYYY-MM-DD"),
                }}
              />
            </Box>

            <Divider />

            {/* Lista de servicios */}
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Servicios
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddService}
                  disabled={!selectedBranch || !selectedCustomer || !selectedDate}
                  size="small"
                >
                  Añadir servicio
                </Button>
              </Box>

              {serviceBookings.length === 0 ? (
                <Alert severity="info">
                  Agregue servicios para este cliente. Puede seleccionar múltiples servicios con diferentes empleados.
                </Alert>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {serviceBookings.map((booking, index) => (
                    <Box
                      key={booking.id}
                      sx={{
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          Servicio {index + 1}
                        </Typography>
                        <IconButton size="small" onClick={() => handleRemoveService(booking.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Selección de servicio */}
                      <Autocomplete
                        options={services}
                        value={booking.service}
                        onChange={(e, newValue) => handleServiceChange(booking.id, newValue)}
                        getOptionLabel={(option) =>
                          `${option.name} - Bs ${option.unitPrice} (${option.unitEstimatedWorkMinutes} min)`
                        }
                        loading={loadingServices}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Servicio *"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: <WorkIcon sx={{ mr: 1, color: "text.secondary" }} />,
                              endAdornment: (
                                <>
                                  {loadingServices ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{ mb: 2 }}
                      />

                      {/* Selección de empleado */}
                      <Autocomplete
                        options={getAvailableEmployees(booking.service)}
                        value={booking.employee}
                        onChange={(e, newValue) => handleEmployeeChange(booking.id, newValue)}
                        getOptionLabel={(option) => `${option.first_name} ${option.last_name || ""}`}
                        loading={loadingEmployees}
                        disabled={!booking.service}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Empleado *"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{ mb: 2 }}
                      />

                      {/* Selección de hora */}
                      <Autocomplete
                        options={booking.availableHours}
                        value={booking.hour}
                        onChange={(e, newValue) => handleHourChange(booking.id, newValue)}
                        getOptionLabel={(option) => option.name || ""}
                        disabled={!booking.employee || booking.availableHours.length === 0}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Hora *"
                            size="small"
                            helperText={
                              booking.employee && booking.availableHours.length === 0
                                ? "No hay horarios disponibles"
                                : ""
                            }
                          />
                        )}
                        sx={{ mb: 2 }}
                      />

                      {/* Método de pago */}
                      <FormControl fullWidth size="small">
                        <InputLabel>Método de Pago *</InputLabel>
                        <Select
                          value={booking.paymentMethod || "Efectivo"}
                          onChange={(e) => handlePaymentMethodChange(booking.id, e.target.value)}
                          label="Método de Pago *"
                        >
                          {paymentMethods.map((method) => (
                            <MenuItem key={method.id} value={method.id}>
                              {method.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Información del servicio */}
                      {booking.service && (
                        <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                          <Chip label={`Bs ${booking.service.unitPrice}`} size="small" color="primary" />
                          <Chip
                            label={`${booking.service.unitEstimatedWorkMinutes} min`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Configuración de WhatsApp */}
            {serviceBookings.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <WhatsAppIcon color="success" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Notificaciones por WhatsApp
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={whatsappSettings.sendOnCreate}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, sendOnCreate: e.target.checked })}
                        color="success"
                      />
                    }
                    label="Enviar WhatsApp al crear reserva"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={whatsappSettings.sendOnFinish}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, sendOnFinish: e.target.checked })}
                        color="success"
                      />
                    }
                    label="Enviar WhatsApp al finalizar servicio"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={whatsappSettings.sendQRCode}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, sendQRCode: e.target.checked })}
                        color="success"
                      />
                    }
                    label="Enviar código QR por WhatsApp"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={whatsappSettings.sendOnCancel}
                        onChange={(e) => setWhatsappSettings({ ...whatsappSettings, sendOnCancel: e.target.checked })}
                        color="success"
                      />
                    }
                    label="Enviar WhatsApp al cancelar reserva"
                  />
                </Box>
              </Box>
            )}

            {/* Total */}
            {serviceBookings.length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Bs {calculateTotal()}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="secondary" disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultiReservasModal;
